import React, { useState } from 'react';
import { 
  Table2, 
  FileText,
  GitBranch,
  Clock,
  Copy,
  Check,
  Play,
  Hash,
  Columns3,
  BarChart3,
  ChevronDown,
  Link2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Layers,
  Filter,
  Download
} from 'lucide-react';

const PublishPanel = ({ tableData, onPublish }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);

  // Generate a random product ID
  const productId = `raw_query_product_${Math.floor(Math.random() * 9000000000) + 1000000000}`;

  // Schema data matching the image
  const schemaData = [
    { 
      column: 'total_healthcare_expenses', 
      type: 'float', 
      nullable: 'No', 
      key: '', 
      uniqueness: 100, 
      completeness: 100,
      description: 'Total amount spent on healthcare-related costs.'
    },
    { 
      column: 'total_outstanding_claims', 
      type: 'integer', 
      nullable: 'No', 
      key: '', 
      uniqueness: 100, 
      completeness: 100,
      description: 'Number of claims that are yet to be resolved or paid.'
    },
    { 
      column: 'total_claims', 
      type: 'integer', 
      nullable: 'No', 
      key: '', 
      uniqueness: 100, 
      completeness: 100,
      description: 'Total number of claims processed.'
    },
    { 
      column: 'total_revenue', 
      type: 'integer', 
      nullable: 'No', 
      key: '', 
      uniqueness: 100, 
      completeness: 100,
      description: 'Total income generated from business activities.'
    },
    { 
      column: 'total_unique_customers', 
      type: 'integer', 
      nullable: 'No', 
      key: '', 
      uniqueness: 100, 
      completeness: 100,
      description: 'Count of distinct customers served.'
    }
  ];

  const sqlQuery = `SELECT total_revenue,
total_healthcare_expenses,
total_outstanding_claims,
total_unique_customers,
total_claims
FROM business_performance
LIMIT 1`;

  const handleCopy = () => {
    navigator.clipboard.writeText(sqlQuery);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { id: 'overview', icon: FileText, label: 'Overview' },
    { id: 'data', icon: Table2, label: 'Data' },
    { id: 'lineage', icon: GitBranch, label: 'Lineage' },
    { id: 'schedule', icon: Clock, label: 'Schedule' },
  ];

  const renderOverviewTab = () => (
    <div className="flex-1 overflow-auto p-5 space-y-6">
      {/* Description Section */}
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-200">Description</h3>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">
            Provides aggregated metrics related to revenue, healthcare expenses, claims, unique customers, and outstanding claims to support financial and operational analysis.
          </p>
        </div>

        {/* Data Quality Card */}
        <div className="w-48 shrink-0 rounded-lg border border-slate-700/50 bg-slate-800/30 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-4 h-4 rounded-full border-2 border-gray-400"></div>
            <span className="text-sm font-medium text-gray-200">Data Quality</span>
          </div>
          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 mb-3">
            GOOD
          </span>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Uniqueness:</span>
              <span className="text-gray-200 font-medium">100%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Completeness:</span>
              <span className="text-gray-200 font-medium">100%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata Row */}
      <div className="flex items-center gap-6 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <Hash className="w-3.5 h-3.5" />
          <span>Rows:</span>
          <span className="text-gray-200 font-medium">25</span>
        </div>
        <div className="flex items-center gap-2">
          <Columns3 className="w-3.5 h-3.5" />
          <span>Columns:</span>
          <span className="text-gray-200 font-medium">15</span>
        </div>
        <div className="flex items-center gap-2">
          <BarChart3 className="w-3.5 h-3.5" />
          <span>Profiling:</span>
          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400">
            COMPLETED
          </span>
        </div>
      </div>

      {/* Schema Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Table2 className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-200">Schema</h3>
        </div>
        <div className="rounded-lg border border-slate-700/50 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800/50 border-b border-slate-700/50">
                <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Column</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Nullable</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Key</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Uniqueness</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Completeness</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody>
              {schemaData.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-800/50 last:border-0 hover:bg-slate-800/30 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-400 font-mono text-[10px]">123</span>
                      <span className="text-blue-400">{row.column}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-block px-2 py-1 rounded bg-slate-700/50 text-gray-300 font-mono text-[11px]">
                      {row.type}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-gray-400">{row.nullable}</td>
                  <td className="px-4 py-4 text-gray-400">{row.key || '-'}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full" 
                          style={{ width: `${row.uniqueness}%` }}
                        ></div>
                      </div>
                      <span className="text-gray-400 text-[11px]">{row.uniqueness}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full" 
                          style={{ width: `${row.completeness}%` }}
                        ></div>
                      </div>
                      <span className="text-gray-400 text-[11px]">{row.completeness}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-gray-400 max-w-xs">{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SQL Query Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-gray-400">&lt;/&gt;</span>
            <h3 className="text-sm font-medium text-gray-200">SQL Query</h3>
          </div>
          <button 
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-700/50 text-xs text-gray-400 hover:text-gray-200 hover:bg-slate-800/50 transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
        <div className="rounded-lg border border-slate-700/50 bg-slate-900/80 p-4 font-mono text-sm overflow-x-auto">
          <pre className="text-gray-300">
            <span className="text-purple-400">SELECT</span> total_revenue,{'\n'}
            total_healthcare_expenses,{'\n'}
            total_outstanding_claims,{'\n'}
            total_unique_customers,{'\n'}
            total_claims{'\n'}
            <span className="text-purple-400">FROM</span> business_performance{'\n'}
            <span className="text-purple-400">LIMIT</span> <span className="text-orange-400">1</span>
          </pre>
        </div>
      </div>
    </div>
  );

  const renderDataTab = () => {
    // Sample data matching the schema with more columns
    const sampleData = [
      { id: 1, customer_id: 'CUST-001', total_revenue: 4523890, total_healthcare_expenses: 234567.89, total_outstanding_claims: 145, total_unique_customers: 8934, total_claims: 2341, region: 'North America', status: 'Active', created_date: '2024-01-15', last_updated: '2024-01-28', avg_claim_value: 1523.45, claim_approval_rate: 94.2, customer_satisfaction: 4.7, processing_time_days: 3.2 },
      { id: 2, customer_id: 'CUST-002', total_revenue: 3891245, total_healthcare_expenses: 198234.56, total_outstanding_claims: 98, total_unique_customers: 7562, total_claims: 1987, region: 'Europe', status: 'Active', created_date: '2024-01-12', last_updated: '2024-01-27', avg_claim_value: 1345.67, claim_approval_rate: 91.8, customer_satisfaction: 4.5, processing_time_days: 4.1 },
      { id: 3, customer_id: 'CUST-003', total_revenue: 5234567, total_healthcare_expenses: 312456.78, total_outstanding_claims: 203, total_unique_customers: 10234, total_claims: 3102, region: 'Asia Pacific', status: 'Active', created_date: '2024-01-10', last_updated: '2024-01-26', avg_claim_value: 1678.90, claim_approval_rate: 96.5, customer_satisfaction: 4.8, processing_time_days: 2.8 },
      { id: 4, customer_id: 'CUST-004', total_revenue: 2987654, total_healthcare_expenses: 156789.12, total_outstanding_claims: 67, total_unique_customers: 5678, total_claims: 1456, region: 'Latin America', status: 'Active', created_date: '2024-01-08', last_updated: '2024-01-25', avg_claim_value: 1123.45, claim_approval_rate: 89.3, customer_satisfaction: 4.3, processing_time_days: 5.2 },
      { id: 5, customer_id: 'CUST-005', total_revenue: 4123456, total_healthcare_expenses: 245678.90, total_outstanding_claims: 134, total_unique_customers: 8123, total_claims: 2234, region: 'North America', status: 'Active', created_date: '2024-01-05', last_updated: '2024-01-24', avg_claim_value: 1456.78, claim_approval_rate: 93.7, customer_satisfaction: 4.6, processing_time_days: 3.5 },
      { id: 6, customer_id: 'CUST-006', total_revenue: 3567890, total_healthcare_expenses: 187654.32, total_outstanding_claims: 89, total_unique_customers: 6789, total_claims: 1789, region: 'Europe', status: 'Inactive', created_date: '2024-01-03', last_updated: '2024-01-23', avg_claim_value: 1234.56, claim_approval_rate: 90.1, customer_satisfaction: 4.2, processing_time_days: 4.8 },
      { id: 7, customer_id: 'CUST-007', total_revenue: 4890123, total_healthcare_expenses: 278901.23, total_outstanding_claims: 178, total_unique_customers: 9456, total_claims: 2678, region: 'Asia Pacific', status: 'Active', created_date: '2024-01-01', last_updated: '2024-01-22', avg_claim_value: 1567.89, claim_approval_rate: 95.2, customer_satisfaction: 4.9, processing_time_days: 2.5 },
      { id: 8, customer_id: 'CUST-008', total_revenue: 3234567, total_healthcare_expenses: 167890.45, total_outstanding_claims: 112, total_unique_customers: 6234, total_claims: 1567, region: 'Middle East', status: 'Active', created_date: '2023-12-28', last_updated: '2024-01-21', avg_claim_value: 1189.23, claim_approval_rate: 88.9, customer_satisfaction: 4.1, processing_time_days: 5.5 },
      { id: 9, customer_id: 'CUST-009', total_revenue: 5678901, total_healthcare_expenses: 345678.90, total_outstanding_claims: 245, total_unique_customers: 11234, total_claims: 3456, region: 'North America', status: 'Active', created_date: '2023-12-25', last_updated: '2024-01-20', avg_claim_value: 1789.01, claim_approval_rate: 97.1, customer_satisfaction: 4.8, processing_time_days: 2.2 },
      { id: 10, customer_id: 'CUST-010', total_revenue: 2789012, total_healthcare_expenses: 145678.23, total_outstanding_claims: 56, total_unique_customers: 5123, total_claims: 1234, region: 'Europe', status: 'Inactive', created_date: '2023-12-22', last_updated: '2024-01-19', avg_claim_value: 1098.76, claim_approval_rate: 86.5, customer_satisfaction: 3.9, processing_time_days: 6.1 },
      { id: 11, customer_id: 'CUST-011', total_revenue: 4012345, total_healthcare_expenses: 223456.78, total_outstanding_claims: 167, total_unique_customers: 7890, total_claims: 2123, region: 'Asia Pacific', status: 'Active', created_date: '2023-12-20', last_updated: '2024-01-18', avg_claim_value: 1412.34, claim_approval_rate: 92.8, customer_satisfaction: 4.4, processing_time_days: 3.8 },
      { id: 12, customer_id: 'CUST-012', total_revenue: 3456789, total_healthcare_expenses: 189012.34, total_outstanding_claims: 123, total_unique_customers: 6543, total_claims: 1876, region: 'Latin America', status: 'Active', created_date: '2023-12-18', last_updated: '2024-01-17', avg_claim_value: 1298.45, claim_approval_rate: 91.2, customer_satisfaction: 4.3, processing_time_days: 4.3 },
      { id: 13, customer_id: 'CUST-013', total_revenue: 5123456, total_healthcare_expenses: 298765.43, total_outstanding_claims: 198, total_unique_customers: 9876, total_claims: 2987, region: 'North America', status: 'Active', created_date: '2023-12-15', last_updated: '2024-01-16', avg_claim_value: 1654.32, claim_approval_rate: 95.9, customer_satisfaction: 4.7, processing_time_days: 2.9 },
      { id: 14, customer_id: 'CUST-014', total_revenue: 2654321, total_healthcare_expenses: 134567.89, total_outstanding_claims: 45, total_unique_customers: 4567, total_claims: 1098, region: 'Middle East', status: 'Inactive', created_date: '2023-12-12', last_updated: '2024-01-15', avg_claim_value: 987.65, claim_approval_rate: 84.3, customer_satisfaction: 3.8, processing_time_days: 6.8 },
      { id: 15, customer_id: 'CUST-015', total_revenue: 4567890, total_healthcare_expenses: 256789.01, total_outstanding_claims: 156, total_unique_customers: 8765, total_claims: 2456, region: 'Europe', status: 'Active', created_date: '2023-12-10', last_updated: '2024-01-14', avg_claim_value: 1523.78, claim_approval_rate: 94.5, customer_satisfaction: 4.6, processing_time_days: 3.3 },
      { id: 16, customer_id: 'CUST-016', total_revenue: 3789012, total_healthcare_expenses: 201234.56, total_outstanding_claims: 134, total_unique_customers: 7123, total_claims: 1923, region: 'Asia Pacific', status: 'Active', created_date: '2023-12-08', last_updated: '2024-01-13', avg_claim_value: 1378.90, claim_approval_rate: 92.1, customer_satisfaction: 4.4, processing_time_days: 4.0 },
      { id: 17, customer_id: 'CUST-017', total_revenue: 4987654, total_healthcare_expenses: 287654.32, total_outstanding_claims: 189, total_unique_customers: 9543, total_claims: 2765, region: 'North America', status: 'Active', created_date: '2023-12-05', last_updated: '2024-01-12', avg_claim_value: 1598.76, claim_approval_rate: 96.2, customer_satisfaction: 4.8, processing_time_days: 2.6 },
      { id: 18, customer_id: 'CUST-018', total_revenue: 3098765, total_healthcare_expenses: 165432.10, total_outstanding_claims: 87, total_unique_customers: 5876, total_claims: 1543, region: 'Latin America', status: 'Active', created_date: '2023-12-03', last_updated: '2024-01-11', avg_claim_value: 1176.54, claim_approval_rate: 89.8, customer_satisfaction: 4.2, processing_time_days: 4.9 },
      { id: 19, customer_id: 'CUST-019', total_revenue: 5432109, total_healthcare_expenses: 321098.76, total_outstanding_claims: 223, total_unique_customers: 10876, total_claims: 3234, region: 'Europe', status: 'Active', created_date: '2023-12-01', last_updated: '2024-01-10', avg_claim_value: 1712.34, claim_approval_rate: 96.8, customer_satisfaction: 4.9, processing_time_days: 2.4 },
      { id: 20, customer_id: 'CUST-020', total_revenue: 2876543, total_healthcare_expenses: 154321.09, total_outstanding_claims: 76, total_unique_customers: 5234, total_claims: 1321, region: 'Middle East', status: 'Inactive', created_date: '2023-11-28', last_updated: '2024-01-09', avg_claim_value: 1054.32, claim_approval_rate: 85.6, customer_satisfaction: 3.7, processing_time_days: 6.5 },
      { id: 21, customer_id: 'CUST-021', total_revenue: 4234567, total_healthcare_expenses: 234567.89, total_outstanding_claims: 145, total_unique_customers: 8234, total_claims: 2198, region: 'Asia Pacific', status: 'Active', created_date: '2023-11-25', last_updated: '2024-01-08', avg_claim_value: 1465.78, claim_approval_rate: 93.4, customer_satisfaction: 4.5, processing_time_days: 3.6 },
      { id: 22, customer_id: 'CUST-022', total_revenue: 3654321, total_healthcare_expenses: 198765.43, total_outstanding_claims: 112, total_unique_customers: 6876, total_claims: 1876, region: 'North America', status: 'Active', created_date: '2023-11-22', last_updated: '2024-01-07', avg_claim_value: 1321.09, claim_approval_rate: 91.5, customer_satisfaction: 4.3, processing_time_days: 4.2 },
      { id: 23, customer_id: 'CUST-023', total_revenue: 5098765, total_healthcare_expenses: 298765.43, total_outstanding_claims: 201, total_unique_customers: 9765, total_claims: 2987, region: 'Europe', status: 'Active', created_date: '2023-11-20', last_updated: '2024-01-06', avg_claim_value: 1643.21, claim_approval_rate: 95.7, customer_satisfaction: 4.7, processing_time_days: 2.8 },
      { id: 24, customer_id: 'CUST-024', total_revenue: 2543210, total_healthcare_expenses: 132109.87, total_outstanding_claims: 54, total_unique_customers: 4321, total_claims: 1087, region: 'Latin America', status: 'Inactive', created_date: '2023-11-18', last_updated: '2024-01-05', avg_claim_value: 976.54, claim_approval_rate: 83.9, customer_satisfaction: 3.6, processing_time_days: 7.1 },
      { id: 25, customer_id: 'CUST-025', total_revenue: 4765432, total_healthcare_expenses: 267890.12, total_outstanding_claims: 167, total_unique_customers: 8976, total_claims: 2543, region: 'Asia Pacific', status: 'Active', created_date: '2023-11-15', last_updated: '2024-01-04', avg_claim_value: 1543.21, claim_approval_rate: 94.8, customer_satisfaction: 4.6, processing_time_days: 3.1 },
    ];

    const columns = ['id', 'customer_id', 'region', 'status', 'total_revenue', 'total_healthcare_expenses', 'total_outstanding_claims', 'total_unique_customers', 'total_claims', 'avg_claim_value', 'claim_approval_rate', 'customer_satisfaction', 'processing_time_days', 'created_date', 'last_updated'];

    const formatValue = (col, value) => {
      if (col === 'total_healthcare_expenses' || col === 'avg_claim_value') {
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      if (col === 'total_revenue') {
        return `$${value.toLocaleString('en-US')}`;
      }
      if (col === 'claim_approval_rate') {
        return `${value}%`;
      }
      if (col === 'processing_time_days') {
        return `${value} days`;
      }
      if (typeof value === 'number') {
        return value.toLocaleString('en-US');
      }
      return value;
    };

    const getStatusBadge = (status) => {
      if (status === 'Active') {
        return <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400">{status}</span>;
      }
      return <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-500/20 text-gray-400">{status}</span>;
    };

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Table Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800/30 border-b border-slate-700/50 shrink-0">
          <span className="text-xs text-gray-400">
            Showing <span className="text-gray-200 font-medium">{sampleData.length}</span> rows
          </span>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-gray-400 hover:text-gray-200 hover:bg-slate-700/50 transition-colors">
              <Columns3 className="w-3.5 h-3.5" />
              Columns
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-800/80 border-b border-slate-700/50">
                {columns.map((col, idx) => (
                  <th 
                    key={idx} 
                    className="px-4 py-3 text-left font-medium text-gray-400 whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sampleData.map((row, rowIdx) => (
                <tr 
                  key={rowIdx} 
                  className="border-b border-slate-800/30 last:border-0 hover:bg-slate-800/30 transition-colors"
                >
                  {columns.map((col, colIdx) => (
                    <td 
                      key={colIdx} 
                      className="px-4 py-3 text-gray-300 whitespace-nowrap font-mono"
                    >
                      {col === 'status' ? getStatusBadge(row[col]) : formatValue(col, row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800/30 border-t border-slate-700/50 shrink-0">
          <span className="text-xs text-gray-400">
            {sampleData.length} rows × {columns.length} columns
          </span>
        </div>
      </div>
    );
  };

  const renderLineageTab = () => {
    const sourceTable = {
      name: 'business_performance',
      location: 'intugle/sink_722',
      columns: [
        { name: 'total_revenue', type: 'DOUBLE', hasLineage: true },
        { name: 'total_unique_customers', type: 'LONG', hasLineage: true },
        { name: 'total_claims', type: 'LONG', hasLineage: true },
        { name: 'total_outstanding_claims', type: 'DOUBLE', hasLineage: true },
        { name: 'total_healthcare_expenses', type: 'DOUBLE', hasLineage: true },
      ]
    };

    const targetTable = {
      name: productId,
      location: 'intugle/sink_722',
      columns: [
        { name: 'total_revenue', type: 'DOUBLE', hasLineage: true },
        { name: 'total_healthcare_expenses', type: 'DOUBLE', hasLineage: true },
        { name: 'total_outstanding_claims', type: 'DOUBLE', hasLineage: true },
        { name: 'total_unique_customers', type: 'LONG', hasLineage: true },
        { name: 'total_claims', type: 'LONG', hasLineage: true },
      ]
    };

    const TableCard = ({ table, isSource }) => (
      <div className="relative bg-slate-900/80 border border-slate-700/50 rounded-xl w-[320px] shadow-xl backdrop-blur-sm">
        {/* Add button */}
        <button className={`absolute top-1/2 -translate-y-1/2 ${isSource ? '-left-3' : '-right-3'} w-6 h-6 rounded-full bg-blue-500 hover:bg-blue-400 flex items-center justify-center text-white text-sm font-bold transition-colors shadow-lg z-20`}>
          +
        </button>
        
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 rounded text-[10px] font-medium bg-slate-800 text-gray-300 flex items-center gap-1.5 border border-slate-700/50">
              <Table2 className="w-3 h-3" />
              TABLE
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ChevronDown className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-white">{table.name}</span>
            <div className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-3 h-3 text-green-400" />
            </div>
          </div>
          <p className="text-xs text-gray-500 ml-6 mt-0.5">{table.location}</p>
        </div>
        
        {/* Columns */}
        <div className="py-1">
          {table.columns.map((col, idx) => (
            <div 
              key={idx} 
              className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-800/50 transition-colors group"
            >
              <div className="flex items-center gap-2.5">
                <Link2 className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-400 font-medium">{col.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 font-mono">{col.type}</span>
                <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center">
                  <Link2 className="w-3 h-3 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-700/50">
          <p className="text-xs text-gray-500">{table.columns.length} columns • {table.columns.filter(c => c.hasLineage).length} with lineage</p>
        </div>
      </div>
    );

    return (
      <div className="flex-1 flex flex-col overflow-hidden" style={{ background: 'linear-gradient(180deg, #0a0f1a 0%, #0d1321 100%)' }}>
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700/50 shrink-0 bg-slate-900/50">
          <div className="flex items-center border border-slate-700/50 rounded-lg overflow-hidden">
            <button className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-200 hover:bg-slate-800/50 transition-colors border-r border-slate-700/50">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-200 hover:bg-slate-800/50 transition-colors border-r border-slate-700/50">
              <ZoomOut className="w-4 h-4" />
            </button>
            <button className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-200 hover:bg-slate-800/50 transition-colors">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700/50 text-sm text-gray-400 hover:text-gray-200 hover:bg-slate-800/50 transition-colors">
            <Layers className="w-4 h-4" />
            Table View
          </button>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700/50 text-sm text-gray-400 hover:text-gray-200 hover:bg-slate-800/50 transition-colors">
            <Filter className="w-4 h-4" />
            Filter
          </button>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700/50 text-sm text-gray-400 hover:text-gray-200 hover:bg-slate-800/50 transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto relative">
          <div className="min-h-full min-w-full p-12 flex items-center justify-center">
            {/* Lineage Flow Container */}
            <div className="relative flex items-center">
              {/* Source Table */}
              <TableCard table={sourceTable} isSource={true} />
              
              {/* Connection Area with SVG */}
              <div className="relative w-[180px] h-[300px] mx-[-20px]">
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 180 300" fill="none">
                  {/* Connection lines from source to target */}
                  {/* Line 1: total_revenue -> total_revenue */}
                  <path 
                    d="M 0 50 C 60 50, 120 30, 180 30" 
                    stroke="#3b82f6" 
                    strokeWidth="2" 
                    strokeOpacity="0.6"
                    fill="none"
                  />
                  {/* Line 2: total_unique_customers -> total_unique_customers */}
                  <path 
                    d="M 0 95 C 60 95, 120 200, 180 200" 
                    stroke="#3b82f6" 
                    strokeWidth="2" 
                    strokeOpacity="0.6"
                    fill="none"
                  />
                  {/* Line 3: total_claims -> total_claims */}
                  <path 
                    d="M 0 140 C 60 140, 120 245, 180 245" 
                    stroke="#3b82f6" 
                    strokeWidth="2" 
                    strokeOpacity="0.6"
                    fill="none"
                  />
                  {/* Line 4: total_outstanding_claims -> total_outstanding_claims */}
                  <path 
                    d="M 0 185 C 60 185, 120 120, 180 120" 
                    stroke="#3b82f6" 
                    strokeWidth="2" 
                    strokeOpacity="0.6"
                    fill="none"
                  />
                  {/* Line 5: total_healthcare_expenses -> total_healthcare_expenses */}
                  <path 
                    d="M 0 230 C 60 230, 120 75, 180 75" 
                    stroke="#3b82f6" 
                    strokeWidth="2" 
                    strokeOpacity="0.6"
                    fill="none"
                  />
                </svg>
                
                {/* Connection Badge in Center */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-0 z-10">
                  <div className="w-7 h-7 rounded-full bg-slate-800 border-2 border-slate-600 flex items-center justify-center shadow-lg">
                    <span className="text-gray-400 text-sm">−</span>
                  </div>
                  <div className="px-2.5 py-1 rounded-full bg-slate-800 border border-slate-600 flex items-center gap-1.5 shadow-lg mx-1">
                    <Link2 className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs text-gray-300 font-medium">5</span>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-slate-800 border-2 border-slate-600 flex items-center justify-center shadow-lg">
                    <span className="text-gray-400 text-sm">−</span>
                  </div>
                </div>
              </div>
              
              {/* Target Table */}
              <TableCard table={targetTable} isSource={false} />
            </div>
          </div>
          
          {/* Legend */}
          <div className="absolute bottom-6 left-6 bg-slate-900/95 border border-slate-700/50 rounded-xl p-4 backdrop-blur-sm shadow-xl">
            <h4 className="text-sm font-medium text-white mb-3">Legend</h4>
            <div className="space-y-2.5">
              <div className="flex items-center gap-3">
                <div className="w-6 h-[2px] bg-blue-500/50 rounded"></div>
                <span className="text-xs text-gray-400">Table Connection (click for details)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-[2px] bg-blue-400 rounded"></div>
                <span className="text-xs text-gray-400">Column Lineage (selected)</span>
              </div>
              <div className="flex items-center gap-3">
                <Link2 className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-gray-400">Has Lineage Connection</span>
              </div>
            </div>
          </div>
          
          {/* Minimap */}
          <div className="absolute bottom-6 right-6 w-36 h-28 bg-slate-900/95 border border-slate-700/50 rounded-xl p-3 backdrop-blur-sm shadow-xl">
            <div className="w-full h-full bg-slate-800/50 rounded-lg flex items-center justify-center gap-4 p-2">
              <div className="w-10 h-14 bg-blue-500/40 rounded border border-blue-500/30"></div>
              <div className="w-10 h-14 bg-blue-500/40 rounded border border-blue-500/30"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderScheduleTab = () => (
    <div className="flex-1 flex items-center justify-center text-gray-500">
      <div className="text-center">
        <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Schedule Configuration</p>
        <p className="text-xs mt-1">Coming soon</p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full bg-slate-900/40 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-700/50 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
              <Table2 className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium text-gray-200 font-mono">{productId}</h2>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400">
                  <Check className="w-3 h-3" />
                  PUBLISHED
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 uppercase">
                  Customer-Data
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 uppercase">
                  Unified-View
                </span>
              </div>
            </div>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-medium transition-colors">
            <Play className="w-4 h-4" />
            Re-run
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 px-5 py-2 border-b border-slate-700/50 shrink-0">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive 
                  ? 'bg-slate-800/50 text-blue-400' 
                  : 'text-gray-400 hover:text-gray-200 hover:bg-slate-800/30'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'data' && renderDataTab()}
      {activeTab === 'lineage' && renderLineageTab()}
      {activeTab === 'schedule' && renderScheduleTab()}
    </div>
  );
};

export default PublishPanel;
